import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {ENTITLEMENT_KEYS} from '@packages/systems/billing/common';
import {notInSiteKinds} from '@packages/domains/site-access/abilities-dimension';

/**
 * This selector determines whether we should hide the Publish
 * button in the Designer top bar
 *
 * Publisher-only custom roles (GOV-3226) have no design/edit site mode
 * access — every branch below would hide the button even though their role
 * grants publishing. The publish-ability branches keep the button visible
 * for those roles; whether the button is *enabled* is decided separately by
 * the publish-permission selectors in the Publish component.
 */
export const canSeePublishButton = defineAbilityConfig([
  {abilities: {canAccessDesignSiteMode: true}},
  {abilities: {canAccessEditSiteMode: true}},
  {abilities: {canFullSitePublish: true}},
  {abilities: {canPagePublish: true}},
  // Release pages resolve as main, so site-mode access can miss staging roles.
  {abilities: {canStageSiteBranch: true}},
] as const);

/**
 * This selector determines whether we should hide the 'Invite content editors'
 * section of the share popover in the Designer.
 * There's no specific permission to gate collaborator invites. We are
 * using the site.update as a stand-in that is true for all roles except
 * for commenter.
 */
export const canInviteCollaborators = defineAbilityConfig({
  permission: ['update', 'site'],
} as const);

// This controls access to page branching features.
export const canAccessPageBranching = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_PAGE_BRANCHING],
  siteKinds: notInSiteKinds('campaign'),
} as const);

// This controls access to private staging features.
export const canAccessPrivateStaging = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_PRIVATE_STAGING],
} as const);

export const canViewExportButton = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  workflows: {canvas: true, webApps: true},
} as const);

export const canExport = defineAbilityConfig({
  abilities: {canViewExportButton: true},
  userTypes: {
    authorizedInDesigner: true,
  },
  permission: ['export', 'site'],
  siteModes: {design: true, edit: true},
} as const);

// This controls access to publishing workflows features.
export const canAccessPublishingWorkflows = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_PUBLISHING_WORKFLOWS],
} as const);

// This blocks branch publishing. Some roles will have the ability
// to publish to branches but not publish the full site.
export const canPublishBranch = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  branches: {
    branch: true,
  },
  permission: ['publish', 'siteBranch'],
} as const);

// This determines whether or not the user can merge a branch into the main page based on their site permission only.
// In order to be able to merge a branch, you must have the state defined in the 'canMergeBranch' ability.
// This ability is used to show a specific message to users w/o site permission to merge a branch only.
// The legacy permission map has no branch scope, so a Release with fine-grained access checks the
// SiteBranch authorization of the active Release instead.
export const hasPermissionToMergeBranch = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
    permission: ['merge', 'siteBranch'],
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    pageScope: ['main', 'pageBranch'],
    permission: ['merge', 'siteBranch'],
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    pageScope: ['siteBranch'],
    authorizations: {'SiteBranch/merge': true},
  },
] as const);

export const canMergeBranchWithAuthorization = defineAbilityConfig([
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      hasUnrestrictedLocaleAccess: true,
    },
    permission: ['merge', 'siteBranch'],
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      hasUnrestrictedLocaleAccess: true,
    },
    authorizations: {'SiteBranch/merge': true},
  },
] as const);

// This determines whether or not the user can merge an approved branch into the main page based on their site permission only.
// In order to be able to merge a branch, you must have the state defined in the 'canMergeApprovedBranch' ability.
// This ability is used to show a specific message to users w/o site permission to merge a branch only.
// Release scoping matches hasPermissionToMergeBranch.
export const hasPermissionToMergeApprovedBranch = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
    permission: ['mergeApproved', 'siteBranch'],
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    pageScope: ['main', 'pageBranch'],
    permission: ['mergeApproved', 'siteBranch'],
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    pageScope: ['siteBranch'],
    authorizations: {'SiteBranch/mergeApproved': true},
  },
] as const);

export const canMergeApprovedBranchWithAuthorization = defineAbilityConfig([
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      hasUnrestrictedLocaleAccess: true,
    },
    permission: ['mergeApproved', 'siteBranch'],
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      hasUnrestrictedLocaleAccess: true,
    },
    authorizations: {'SiteBranch/mergeApproved': true},
  },
] as const);

// This determines whether or not the user can merge a branch into the main page.
export const canMergeBranch = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true},
    abilities: {canMergeBranchWithAuthorization: true},
  },
] as const);

// This determines whether or not the user can merge an approved branch into the main page.
export const canMergeApprovedBranch = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true},
    abilities: {canMergeApprovedBranchWithAuthorization: true},
  },
] as const);

export const canApproveReview = defineAbilityConfig([
  {
    permission: ['approve', 'site'],
  },
] as const);

export const canCancelReview = defineAbilityConfig([
  {
    permissions: [
      ['approve', 'site'],
      ['design', 'siteBranch'],
    ],
  },
] as const);

export const canAccessBranchReviews = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_BRANCH_APPROVAL_WORKFLOW],
} as const);

export const canAccessAdvancedPublishSettings = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  siteModes: {design: true},
} as const);

/**
 * Defines if a user can export a component
 * This is necessary for page builder work as on edit mode users
 * who are part of the page build beta now have access to components panel
 */
export const canExportComponents = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  siteModes: {
    design: true,
  },
} as const);

export const canAccessPageCustomCode = defineAbilityConfig({
  siteModes: {design: true},
} as const);
